import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { apiService } from '../services/apiService';

const MetricCard: React.FC<{ title: string; value: number | string }> = ({ title, value }) => (
  <Card>
    <CardContent>
      <Typography variant="body2" color="text.secondary">{title}</Typography>
      <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>{value}</Typography>
    </CardContent>
  </Card>
);

const SuperAdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [advancedSummary, setAdvancedSummary] = useState<any>(null);
  const [advancedAlerts, setAdvancedAlerts] = useState<any[]>([]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const [data, advSummary, advAlerts] = await Promise.all([
          apiService.getAdminDashboardSummary(),
          apiService.getAdminAdvancedMonitoringSummary({ days: 14 }),
          apiService.getAdminAdvancedMonitoringAlerts({ failedLoginThreshold: 5, spikeMultiplier: 3.0 }),
        ]);
        setSummary(data);
        setAdvancedSummary(advSummary);
        setAdvancedAlerts(Array.isArray(advAlerts) ? advAlerts : []);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load dashboard summary');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const suspiciousAlerts = useMemo(
    () => (advancedAlerts || []).filter((a: any) => a?.type && a?.severity),
    [advancedAlerts]
  );

  if (loading) return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>📊 Super Admin Global Visibility</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}><MetricCard title="Total Users" value={summary?.totalUsers || 0} /></Grid>
        <Grid item xs={12} md={3}><MetricCard title="Active Users (24h)" value={summary?.activeUsers24h || 0} /></Grid>
        <Grid item xs={12} md={3}><MetricCard title="Inactive Users (24h)" value={summary?.inactiveUsers24h || 0} /></Grid>
        <Grid item xs={12} md={3}><MetricCard title="Total Projects" value={summary?.totalProjects || 0} /></Grid>

        <Grid item xs={12} md={3}><MetricCard title="Environments Count" value={summary?.environmentCount || 0} /></Grid>
        <Grid item xs={12} md={3}><MetricCard title="Total Test Cycles" value={summary?.totalTestCyclesExecuted || 0} /></Grid>
        <Grid item xs={12} md={3}><MetricCard title="Proxy Downloads (Total)" value={summary?.totalProxyDownloads || 0} /></Grid>
        <Grid item xs={12} md={3}><MetricCard title="Failed Login Alerts" value={summary?.failedLoginAttempts || 0} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Login Trends</Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={summary?.loginTrends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="successCount" stackId="a" fill="#2e7d32" name="Success" />
                  <Bar dataKey="failedCount" stackId="a" fill="#d32f2f" name="Failed" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>User Activity Frequency</Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={summary?.activityTrends || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#2e7d32" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>System Usage: Proxy Downloads</Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={summary?.proxyDownloadsOverTime || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#ed6c02" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Advanced Monitoring</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Box sx={{ minWidth: 240, flex: '1 1 240px' }}>
                <Typography variant="body2" color="text.secondary">Suspicious Activities</Typography>
                <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>{summary?.suspiciousActivities || 0}</Typography>
              </Box>
              <Box sx={{ minWidth: 240, flex: '1 1 240px' }}>
                <Typography variant="body2" color="text.secondary">Suspicious threshold</Typography>
                <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>Auto-detected (device)</Typography>
                <Typography variant="caption" color="text.secondary">Based on suspicious login signals in `user_devices`.</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Suspicious Activities (Alerts)</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {suspiciousAlerts.slice(0, 8).map((a: any, idx: number) => (
                <Box key={`${a.type}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {a.type}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {a.severity}
                  </Typography>
                </Box>
              ))}
              {suspiciousAlerts.length === 0 && (
                <Typography variant="body2" color="text.secondary">No alerts detected in the current window.</Typography>
              )}
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Most Active Users</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(summary?.topActiveUsers || []).map((u: any) => (
                <Box key={u.user_id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {u.email || `user-${u.user_id}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {u.activity_count} actions
                  </Typography>
                </Box>
              ))}
              {(summary?.topActiveUsers || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No activity data yet.</Typography>
              )}
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Most Used Projects</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(summary?.mostUsedProjects || []).map((p: any) => (
                <Box key={p.project_id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {p.project_name || `project-${p.project_id}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {p.usage_count} uses
                  </Typography>
                </Box>
              ))}
              {(summary?.mostUsedProjects || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No project usage data yet.</Typography>
              )}
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Proxy Downloads per User (14d)</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(advancedSummary?.proxyDownloadsPerUser || []).slice(0, 8).map((r: any) => (
                <Box key={r.userId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.userEmail || `user-${r.userId}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.downloadCount} downloads
                  </Typography>
                </Box>
              ))}
              {(advancedSummary?.proxyDownloadsPerUser || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No proxy downloads in the last 14 days.</Typography>
              )}
            </Box>

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Tool Usage Frequency (14d)</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(advancedSummary?.toolUsageFrequency || []).slice(0, 8).map((r: any, idx: number) => (
                <Box key={`${r.actionType}-${idx}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {r.actionType || 'UNKNOWN'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {r.usageCount} events
                  </Typography>
                </Box>
              ))}
              {(advancedSummary?.toolUsageFrequency || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No activity recorded in the last 14 days.</Typography>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SuperAdminDashboard;
